import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Users, Star, MessageCircle, Heart, TrendingUp, Calendar, BarChart3, Package, X, ChevronRight, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API, CIGAR_PLACEHOLDER } from '../config/constants';
import BottomNav from '../components/BottomNav';
import useModalHistory from '../hooks/useModalHistory';

const AnalyticsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('this_month');
  
  // Modal state for clickable stats
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalData, setModalData] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Handle browser back button for modal
  const { closeModal } = useModalHistory(modalOpen, () => setModalOpen(false), 'analytics-modal');

  const fetchAnalytics = useCallback(async () => {
    try {
      setError(false);
      const res = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError(true);
      if (err.response?.status === 403) {
        navigate('/profile');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const getPeriodData = () => {
    if (!analytics) return null;
    switch (selectedPeriod) {
      case 'today':
        return analytics.today;
      case 'this_week':
        return analytics.this_week;
      case 'this_month':
        return analytics.this_month;
      default:
        return analytics.totals;
    }
  };

  const periodData = getPeriodData();

  // Get date filter based on selected period
  const getDateFilter = () => {
    const now = new Date();
    switch (selectedPeriod) {
      case 'today':
        // Use UTC date to match backend
        const todayStart = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        return todayStart;
      case 'this_week':
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        return weekAgo;
      case 'this_month':
        const monthAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        return monthAgo;
      default:
        return null; // all_time - no filter
    }
  };

  // Filter data by date
  const filterByDate = (data) => {
    const dateFilter = getDateFilter();
    if (!dateFilter) return data; // all_time - return all data
    
    return data.filter(item => {
      // Parse the ISO date string and compare
      const itemDate = new Date(item.created_at);
      return itemDate >= dateFilter;
    });
  };

  // Handle stat card click
  const handleStatClick = async (type) => {
    setModalType(type);
    setModalOpen(true);
    setModalLoading(true);
    
    try {
      let endpoint = '';
      switch (type) {
        case 'cigars':
          endpoint = '/admin/all-cigars';
          break;
        case 'users':
          endpoint = '/admin/users';
          break;
        case 'ratings':
          endpoint = '/admin/all-ratings';
          break;
        case 'comments':
          endpoint = '/admin/all-comments';
          break;
        case 'favorites':
          endpoint = '/admin/all-favorites';
          break;
        case 'visits':
          endpoint = '/admin/all-visits';
          break;
        default:
          return;
      }
      
      // Add period parameter to filter server-side (except for all_time and visits)
      const periodParam = selectedPeriod !== 'all_time' && type !== 'visits' 
        ? `?period=${selectedPeriod}` 
        : '';
      
      const res = await axios.get(`${API}${endpoint}${periodParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // For visits, still filter client-side since the endpoint handles it differently
      const data = type === 'visits' && selectedPeriod !== 'all_time' 
        ? filterByDate(res.data) 
        : res.data;
      setModalData(data);
    } catch (err) {
      toast.error(`Failed to fetch ${type}`);
      setModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  // Get period label for modal title
  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'today': return 'Today';
      case 'this_week': return 'This Week';
      case 'this_month': return 'This Month';
      default: return 'All Time';
    }
  };

  const getModalTitle = () => {
    const periodLabel = getPeriodLabel();
    switch (modalType) {
      case 'cigars': return `Cigars Added - ${periodLabel}`;
      case 'users': return `Users - ${periodLabel}`;
      case 'ratings': return `Ratings - ${periodLabel}`;
      case 'comments': return `Comments - ${periodLabel}`;
      case 'favorites': return `Favorites - ${periodLabel}`;
      case 'visits': return `Unique Visitors - ${periodLabel}`;
      default: return '';
    }
  };

  const getModalIcon = () => {
    switch (modalType) {
      case 'cigars': return <Package className="text-orange-400" size={24} />;
      case 'users': return <Users className="text-blue-400" size={24} />;
      case 'ratings': return <Star className="text-amber-400" size={24} />;
      case 'comments': return <MessageCircle className="text-green-400" size={24} />;
      case 'favorites': return <Heart className="text-pink-400" size={24} />;
      case 'visits': return <Eye className="text-cyan-400" size={24} />;
      default: return null;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex flex-col items-center justify-center gap-4">
        <div className="text-white/50">Failed to load analytics</div>
        <button 
          onClick={() => { setLoading(true); fetchAnalytics(); }}
          className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded-lg"
        >
          Retry
        </button>
        <button 
          onClick={() => navigate('/admin')}
          className="text-white/50 hover:text-white"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      {/* Header */}
      <div className="flex items-center p-4 border-b border-white/10">
        <button onClick={() => navigate('/admin')} className="text-white mr-4">
          <ArrowLeft size={24} />
        </button>
        <h1 className="playfair text-xl text-white flex-1 text-center pr-8 flex items-center justify-center gap-2">
          <BarChart3 className="text-amber-500" size={24} />
          Analytics
        </h1>
      </div>

      {/* Period Selector */}
      <div className="flex gap-2 p-4 overflow-x-auto">
        {[
          { key: 'today', label: 'Today' },
          { key: 'this_week', label: 'This Week' },
          { key: 'this_month', label: 'This Month' },
          { key: 'all_time', label: 'All Time' }
        ].map(period => (
          <button
            key={period.key}
            onClick={() => setSelectedPeriod(period.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedPeriod === period.key
                ? 'bg-amber-500 text-black'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            {period.label}
          </button>
        ))}
      </div>

      {/* Stats Grid */}
      <div className="p-4 grid grid-cols-2 gap-3">
        <button 
          onClick={() => handleStatClick('users')}
          className="bg-gradient-to-br from-blue-900/30 to-blue-700/20 border border-blue-500/30 rounded-xl p-4 text-left hover:from-blue-900/50 hover:to-blue-700/30 transition-all active:scale-95"
        >
          <div className="flex items-center gap-2 mb-2">
            <Users size={20} className="text-blue-400" />
            <span className="text-white/50 text-sm">Account Creations</span>
          </div>
          <p className="text-3xl font-bold text-blue-400">
            {selectedPeriod === 'all_time' ? analytics?.totals?.users : periodData?.users || 0}
          </p>
        </button>

        <button 
          onClick={() => handleStatClick('ratings')}
          className="bg-gradient-to-br from-amber-900/30 to-amber-700/20 border border-amber-500/30 rounded-xl p-4 text-left hover:from-amber-900/50 hover:to-amber-700/30 transition-all active:scale-95"
        >
          <div className="flex items-center gap-2 mb-2">
            <Star size={20} className="text-amber-400" />
            <span className="text-white/50 text-sm">Cigar Rankings</span>
          </div>
          <p className="text-3xl font-bold text-amber-400">
            {selectedPeriod === 'all_time' ? analytics?.totals?.ratings : periodData?.ratings || 0}
          </p>
        </button>

        <button 
          onClick={() => handleStatClick('cigars')}
          className="bg-gradient-to-br from-orange-900/30 to-orange-700/20 border border-orange-500/30 rounded-xl p-4 text-left hover:from-orange-900/50 hover:to-orange-700/30 transition-all active:scale-95"
        >
          <div className="flex items-center gap-2 mb-2">
            <Package size={20} className="text-orange-400" />
            <span className="text-white/50 text-sm">Cigars Added</span>
          </div>
          <p className="text-3xl font-bold text-orange-400">
            {selectedPeriod === 'all_time' ? analytics?.totals?.cigars : periodData?.cigars || 0}
          </p>
        </button>

        <button 
          onClick={() => handleStatClick('comments')}
          className="bg-gradient-to-br from-green-900/30 to-green-700/20 border border-green-500/30 rounded-xl p-4 text-left hover:from-green-900/50 hover:to-green-700/30 transition-all active:scale-95"
        >
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle size={20} className="text-green-400" />
            <span className="text-white/50 text-sm">Comments</span>
          </div>
          <p className="text-3xl font-bold text-green-400">
            {selectedPeriod === 'all_time' ? analytics?.totals?.comments : periodData?.comments || 0}
          </p>
        </button>

        <button 
          onClick={() => handleStatClick('favorites')}
          className="bg-gradient-to-br from-pink-900/30 to-pink-700/20 border border-pink-500/30 rounded-xl p-4 text-left hover:from-pink-900/50 hover:to-pink-700/30 transition-all active:scale-95"
        >
          <div className="flex items-center gap-2 mb-2">
            <Heart size={20} className="text-pink-400" />
            <span className="text-white/50 text-sm">Favorites</span>
          </div>
          <p className="text-3xl font-bold text-pink-400">
            {selectedPeriod === 'all_time' ? analytics?.totals?.favorites : periodData?.favorites || 0}
          </p>
        </button>

        <button 
          onClick={() => handleStatClick('visits')}
          className="bg-gradient-to-br from-cyan-900/30 to-cyan-700/20 border border-cyan-500/30 rounded-xl p-4 text-left hover:from-cyan-900/50 hover:to-cyan-700/30 transition-all active:scale-95"
        >
          <div className="flex items-center gap-2 mb-2">
            <Eye size={20} className="text-cyan-400" />
            <span className="text-white/50 text-sm">Page Views</span>
          </div>
          <p className="text-3xl font-bold text-cyan-400">
            {selectedPeriod === 'all_time' ? analytics?.totals?.visits : periodData?.visits || 0}
          </p>
        </button>
      </div>

      {/* All Time Totals Summary */}
      {selectedPeriod !== 'all_time' && (
        <div className="mx-4 p-4 bg-white/5 border border-white/10 rounded-xl mb-4">
          <h3 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
            <Calendar size={16} />
            All Time Totals
          </h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-xl font-bold text-white">{analytics?.totals?.users || 0}</p>
              <p className="text-white/40 text-xs">Users</p>
            </div>
            <div>
              <p className="text-xl font-bold text-white">{analytics?.totals?.ratings || 0}</p>
              <p className="text-white/40 text-xs">Ratings</p>
            </div>
            <div>
              <p className="text-xl font-bold text-white">{analytics?.totals?.cigars || 0}</p>
              <p className="text-white/40 text-xs">Cigars</p>
            </div>
          </div>
        </div>
      )}

      {/* Top Cigars */}
      <div className="mx-4 mb-4">
        <h3 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
          <TrendingUp size={16} className="text-amber-500" />
          Most Rated Cigars
        </h3>
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {analytics?.top_cigars?.slice(0, 5).map((cigar, idx) => (
            <div 
              key={cigar.cigar_id}
              onClick={() => navigate(`/cigar/${cigar.cigar_id}`)}
              className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                idx === 0 ? 'bg-amber-500 text-black' : 'bg-white/10 text-white/50'
              }`}>
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{cigar.brand} {cigar.name}</p>
              </div>
              <span className="text-amber-500 text-sm font-medium">{cigar.ratings_count} ratings</span>
            </div>
          ))}
          {(!analytics?.top_cigars || analytics.top_cigars.length === 0) && (
            <div className="p-4 text-center text-white/40 text-sm">No rating data yet</div>
          )}
        </div>
      </div>

      {/* Top Users */}
      <div className="mx-4 mb-4">
        <h3 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
          <Users size={16} className="text-blue-500" />
          Most Active Users
        </h3>
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          {analytics?.top_users?.slice(0, 5).map((user, idx) => (
            <div 
              key={user.user_id}
              onClick={() => navigate(`/user/${user.user_id}`)}
              className="flex items-center gap-3 p-3 border-b border-white/5 last:border-0 hover:bg-white/5 cursor-pointer transition-colors"
            >
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                idx === 0 ? 'bg-blue-500 text-white' : 'bg-white/10 text-white/50'
              }`}>
                {idx + 1}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{user.username}</p>
              </div>
              <span className="text-blue-400 text-sm font-medium">{user.ratings_count} ratings</span>
            </div>
          ))}
          {(!analytics?.top_users || analytics.top_users.length === 0) && (
            <div className="p-4 text-center text-white/40 text-sm">No user data yet</div>
          )}
        </div>
      </div>

      {/* Data Modal */}
      {modalOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div 
            className="bg-[#141416] rounded-2xl w-full max-w-2xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                {getModalIcon()}
                <h2 className="text-white text-lg font-bold">{getModalTitle()}</h2>
                <span className="text-white/50 text-sm">({modalData.length})</span>
              </div>
              <button onClick={() => setModalOpen(false)} className="text-white/50 hover:text-white p-2">
                <X size={24} />
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {modalLoading ? (
                <div className="text-center py-8 text-white/50">Loading...</div>
              ) : modalData.length === 0 ? (
                <div className="text-center py-8 text-white/50">No data found</div>
              ) : (
                <div className="space-y-2">
                  {/* Cigars List */}
                  {modalType === 'cigars' && modalData.map((cigar) => (
                    <button
                      key={cigar.id}
                      onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation();
                        setModalOpen(false); 
                        navigate(`/cigar/${cigar.id}`); 
                      }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center gap-3 text-left transition-colors cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                        <img src={cigar.image_url || CIGAR_PLACEHOLDER} alt={cigar.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-orange-400 text-xs">{cigar.brand}</p>
                        <p className="text-white font-medium truncate">{cigar.name}</p>
                        <p className="text-white/40 text-xs">{cigar.strength} · {cigar.origin}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star size={14} className="text-amber-500" />
                        <span className="text-amber-500 text-sm">{cigar.average_rating?.toFixed(1) || '0.0'}</span>
                      </div>
                      <ChevronRight size={18} className="text-white/30" />
                    </button>
                  ))}

                  {/* Users List */}
                  {modalType === 'users' && modalData.map((user) => (
                    <button
                      key={user.id}
                      onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation();
                        setModalOpen(false); 
                        navigate(`/user/${user.id}`); 
                      }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center gap-3 text-left transition-colors cursor-pointer"
                    >
                      {user.profile_pic ? (
                        <img src={user.profile_pic} alt={user.username} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                          {user.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium">{user.username}</p>
                        <p className="text-white/40 text-xs">{user.email}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-amber-500">{user.ratings_count || 0} ratings</p>
                        <p className="text-white/50">{user.comments_count || 0} comments</p>
                      </div>
                      <ChevronRight size={18} className="text-white/30" />
                    </button>
                  ))}

                  {/* Ratings List */}
                  {modalType === 'ratings' && modalData.map((rating) => (
                    <button
                      key={rating.id}
                      onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation();
                        setModalOpen(false); 
                        navigate(`/cigar/${rating.cigar_id}`); 
                      }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 text-left transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-amber-500 font-medium">{rating.username}</span>
                        <div className="flex items-center gap-1 bg-amber-500/20 px-2 py-0.5 rounded">
                          <Star size={12} className="text-amber-500 fill-amber-500" />
                          <span className="text-amber-500 font-bold text-sm">{rating.rating}</span>
                        </div>
                      </div>
                      <p className="text-white text-sm">{rating.cigar_brand} {rating.cigar_name}</p>
                      <p className="text-white/40 text-xs mt-1">{formatDate(rating.created_at)}</p>
                    </button>
                  ))}

                  {/* Comments List */}
                  {modalType === 'comments' && modalData.map((comment) => (
                    <button
                      key={comment.id}
                      onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation();
                        setModalOpen(false); 
                        navigate(`/cigar/${comment.cigar_id}`); 
                      }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 text-left transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-green-400 font-medium">{comment.username}</span>
                        <span className="text-white/40 text-xs">{formatDate(comment.created_at)}</span>
                      </div>
                      <p className="text-white/50 text-xs mb-1">{comment.cigar_brand} {comment.cigar_name}</p>
                      <p className="text-white text-sm line-clamp-2">{comment.text}</p>
                    </button>
                  ))}

                  {/* Favorites List */}
                  {modalType === 'favorites' && modalData.map((favorite) => (
                    <button
                      key={favorite.id}
                      onClick={(e) => { 
                        e.preventDefault();
                        e.stopPropagation();
                        setModalOpen(false); 
                        navigate(`/cigar/${favorite.cigar_id}`); 
                      }}
                      className="w-full bg-white/5 hover:bg-white/10 rounded-lg p-3 flex items-center gap-3 text-left transition-colors cursor-pointer"
                    >
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                        <img src={favorite.cigar_image || CIGAR_PLACEHOLDER} alt={favorite.cigar_name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-pink-400 text-xs">{favorite.username}</p>
                        <p className="text-white font-medium truncate">{favorite.cigar_brand} {favorite.cigar_name}</p>
                        <p className="text-white/40 text-xs">{formatDate(favorite.created_at)}</p>
                      </div>
                      <Heart size={18} className="text-pink-500 fill-pink-500" />
                    </button>
                  ))}

                  {/* Visits List */}
                  {modalType === 'visits' && modalData.map((visit) => (
                    <div
                      key={visit.id}
                      className="w-full bg-white/5 rounded-lg p-3 text-left"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-cyan-400 font-medium font-mono">{visit.ip_address || 'Unknown'}</span>
                        <span className="text-white/40 text-xs">{formatDate(visit.first_visit)}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-white text-sm">
                          Entry: <span className="text-white/70">{visit.first_page || '/'}</span>
                        </p>
                        <span className="text-white/50 text-xs bg-white/10 px-2 py-0.5 rounded">
                          {visit.visit_count} page{visit.visit_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {visit.referrer && (
                        <p className="text-white/40 text-xs truncate mt-1">From: {visit.referrer}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default AnalyticsPage;
